// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperProps,
    FlexWrapperStyles,
    flexWrapperStyles,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { formActionsStyles } from "@store/Library/Components/Toolkit/FormActions";
import OrderStickyAction, {
    orderStickyActionBreakpointArguments,
} from "@store/Library/Components/Toolkit/Order/OrderStickyAction";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import clearEmptyChild from "@toolkit/Framework/Common/Utilities/react/clearEmptyChild";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { Children, FC, PropsWithChildren, useMemo } from "react";
import { css, useTheme } from "styled-components";

export interface OrderActionsPresentationProps extends ExpandStyles<OrderActionsStyles> {
    gap?: FlexWrapperProps["gap"];
    verticalGap?: FlexWrapperProps["verticalGap"];
}

export interface OrderActionsProps extends OrderActionsPresentationProps {}

export interface OrderActionsStyles {
    flexWrapper?: FlexWrapperStyles;
    stickyItem?: InjectableCss;
}

export const orderActionsStyles: OrderActionsStyles = {
    flexWrapper: {
        wrapper: {
            css: spacingStyles(pxToRem(16)),
        },
        item: {
            css: css`
                ${flexWrapperStyles.item?.css}
                ${formActionsStyles.flexWrapper?.item?.css}
            `,
        },
    },
    stickyItem: {
        css: css`
            ${breakpoint(...orderStickyActionBreakpointArguments)} {
                margin: 0;
            }
        `,
    },
};

const OrderActions: FC<PropsWithChildren<OrderActionsProps>> = ({ extendedStyles, overridedStyles, ...props }) => {
    const baseStyles = useExpandProps(orderActionsStyles, extendedStyles, overridedStyles);
    const theme = useTheme();

    const gap = props.gap || theme.design.form.actions.gap;
    const verticalGap = props.verticalGap || theme.design.form.actions.verticalGap;

    const children = clearEmptyChild(props.children);
    const childrenCount = Children.count(children);
    const hasOtherContent =
        Boolean(childrenCount) && Children.toArray(children).some((x: any) => x.type?.name !== OrderStickyAction.name);

    const styles = useMemo(() => {
        const itemCss = flexWrapperGapStyles(gap, verticalGap);

        return mergeProps<OrderActionsStyles>(
            {
                flexWrapper: {
                    wrapper: {
                        css: css`
                            ${() => {
                                if (!hasOtherContent) {
                                    return css`
                                        ${breakpoint(...orderStickyActionBreakpointArguments)} {
                                            margin: 0;
                                        }
                                    `;
                                }
                            }}
                        `,
                    },
                    item: {
                        css: itemCss,
                    },
                },
                stickyItem: {
                    css: css`
                        ${itemCss}
                        ${baseStyles.flexWrapper?.item?.css}
                    `,
                },
            },
            baseStyles,
        );
    }, [gap, verticalGap, hasOtherContent, baseStyles]);

    if (!childrenCount) {
        return null;
    }

    return (
        <FlexWrapper gap={gap} verticalGap={verticalGap} wrap={false} extendedStyles={styles.flexWrapper}>
            {Children.map(children, (item: any) => {
                if (item) {
                    const isSticky = item.type?.name === OrderStickyAction.name;

                    return (
                        <StyledWrapper {...(isSticky ? styles.stickyItem : styles.flexWrapper?.item)}>
                            {item}
                        </StyledWrapper>
                    );
                }

                return null;
            })}
        </FlexWrapper>
    );
};

export default OrderActions;
